/**
 * 推流相关常量配置
 */

/**
 * 推流错误码（onError 回调）
 * 来源：腾讯云官方文档
 */
export const PUSHER_ERROR_CODES = {
  /** WebRTC 接口调用失败 */
  WEBRTC_FAILED: -1,
  /** 请求服务器推流接口返回报错 */
  REQUEST_FAILED: -2,
  /** 推流 SDK 未加载成功 */
  SDK_NOT_AVAILABLE: -3,
} as const;

/**
 * 推流警告码（onWarning 回调）
 * 来源：腾讯云官方文档
 */
export const PUSHER_WARNING_CODES = {
  /** 打开摄像头失败 */
  CAMERA_START_FAILED: -1001,
  /** 打开麦克风失败 */
  MICROPHONE_START_FAILED: -1002,
  /** 打开屏幕分享失败 */
  SCREEN_CAPTURE_START_FAILED: -1003,
  /** 打开本地媒体文件失败 */
  VIRTUAL_CAMERA_START_FAILED: -1004,
  /** 摄像头被中断（设备被拔出或者权限被用户取消） */
  CAMERA_INTERRUPTED: -1005,
  /** 麦克风被中断（设备被拔出或者权限被用户取消） */
  MICROPHONE_INTERRUPTED: -1006,
  /** 屏幕分享被中断（Chrome浏览器点击自带的停止共享按钮） */
  SCREEN_CAPTURE_INTERRUPTED: -1007,
} as const;

/**
 * 需要提示用户开启摄像头/麦克风的警告码列表
 */
export const CAMERA_REQUIRED_ERROR_CODES = [
  PUSHER_WARNING_CODES.CAMERA_START_FAILED,
  PUSHER_WARNING_CODES.MICROPHONE_START_FAILED,
  PUSHER_WARNING_CODES.CAMERA_INTERRUPTED,
  PUSHER_WARNING_CODES.MICROPHONE_INTERRUPTED,
];

/**
 * 视频质量配置
 */
export const VIDEO_QUALITY_CONFIG = {
  /** 视频质量等级 */
  QUALITY: '720p' as const,
  /** 视频帧率（FPS） */
  FPS: 20,
  /** 视频码率（kbps） */
  BITRATE: 800,
} as const;

/**
 * 音频质量配置
 */
export const AUDIO_QUALITY_CONFIG = {
  /** 音频质量等级 */
  QUALITY: 'standard' as const,
  /** 启用回声消除（AEC） */
  ENABLE_AEC: true,
  /** 启用噪声抑制（ANS） */
  ENABLE_ANS: true,
} as const;

/**
 * 视频分辨率配置
 */
export const VIDEO_RESOLUTION_CONFIG = {
  /** 横屏模式分辨率 */
  LANDSCAPE: {
    width: 1280,
    height: 720,
  },
  /** 竖屏模式分辨率 */
  PORTRAIT: {
    width: 480,
    height: 848,
  },
} as const;

/**
 * 错误和警告消息映射
 */
export const ERROR_MESSAGES = {
  // 错误码消息
  [PUSHER_ERROR_CODES.WEBRTC_FAILED]: 'WebRTC 接口调用失败',
  [PUSHER_ERROR_CODES.REQUEST_FAILED]: '推流服务器连接失败，请检查网络',
  [PUSHER_ERROR_CODES.SDK_NOT_AVAILABLE]:
    '视频推流组件加载失败，请刷新页面或检查网络',

  // 警告码消息
  [PUSHER_WARNING_CODES.CAMERA_START_FAILED]:
    '摄像头启动失败，请检查设备或权限',
  [PUSHER_WARNING_CODES.MICROPHONE_START_FAILED]:
    '麦克风启动失败，请检查设备或权限',
  [PUSHER_WARNING_CODES.SCREEN_CAPTURE_START_FAILED]: '屏幕分享启动失败',
  [PUSHER_WARNING_CODES.VIRTUAL_CAMERA_START_FAILED]: '媒体文件加载失败',
  [PUSHER_WARNING_CODES.CAMERA_INTERRUPTED]:
    '摄像头已中断，请检查设备连接或权限',
  [PUSHER_WARNING_CODES.MICROPHONE_INTERRUPTED]:
    '麦克风已中断，请检查设备连接或权限',
  [PUSHER_WARNING_CODES.SCREEN_CAPTURE_INTERRUPTED]: '屏幕分享已停止',

  DEFAULT: '推流发生错误，请稍后重试',
} as const;

/**
 * 需要立即中断推流流程的错误码列表
 */
export const FATAL_PUSHER_ERROR_CODES = [
  PUSHER_ERROR_CODES.SDK_NOT_AVAILABLE,
] as const;
